import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    Button,
    HelpTooltip,
    Input,
    Modal,
    Badge,
    Select,
    CopyableId,
    Tabs,
    ConfirmDialog,
    Loading,
} from '@/kit';
import { Label } from '../../../components/ui/label';
import { platformApi } from '../../../api/endpoints/platform';
import { tenantApi } from '../../../api/endpoints/tenant';
import { handleApiError } from '../../../api/errorMapping';
import { useAuth } from '../../../hooks/useAuth';
import { useApplication } from '../../../hooks/useApplications';
import {
    byoidOptionsFromLevels,
    DEFAULT_TOKEN_VALIDATION_MODE,
    TOKEN_VALIDATION_MODE_META,
} from '../../../api/types/PlatformLocal';
import type {
    ByoidLevelOption,
    ExternalScopeLevelLocal,
    PlatformTenantListItemLocal,
    TokenValidationModeLocal,
    UpdateTenantRequestLocal,
} from '../../../api/types/PlatformLocal';
import type { MfaMethodLocal } from '../../../api/types/TenantLocal';
import { ExternalScopeLevelsEditor } from '../../../components/tenant/ExternalScopeLevelsEditor';
import { EditTenantSecuritySection } from './EditTenantSecuritySection';
import { securityCannotSave, type SecurityDraft } from '../../../lib/securityPolicy';
import {
    EditTenantAutoregisterSection,
    EMPTY_AUTOREGISTER,
    autoregisterDraftToBody,
    unknownAutoregisterRoleIds,
    type AutoregisterDraft,
} from './EditTenantAutoregisterSection';

// Campos mutables del tenant, consolidados en este modal (DEC-RBAC-049). General → PUT /platform/tenants/{id};
// Seguridad → PUT /tenants/{id}/security-policy; Autoregistro → PUT /tenants/{id}/autoregister.
// DEC-060: emailDomain quedó fuera del modelo (el login es por membresía, no por dominio de email).

// Niveles BYOID válidos para el form. DEC-RBAC-091 (Fase 5): removidos los legacy GROUP/RETAILER/STORE;
// quedan los genéricos LEVEL_1/2/3. NO incluimos `PLATFORM`/`TENANT` (no son niveles BYOID). El `satisfies`
// ata el tuple al enum generado: si el contrato suma/quita un nivel BYOID, `tsc` rompe acá (no a ciegas).
const BYOID_LEVEL_VALUES = [
    'LEVEL_1',
    'LEVEL_2',
    'LEVEL_3',
] as const satisfies readonly ExternalScopeLevelLocal[];

const schema = z.object({
    displayName: z.string().min(1, 'Requerido').max(120),
    tokenValidationMode: z.enum(['offline', 'online_preferred', 'online_strict']),
    // DEC-RBAC-027: niveles BYOID prendidos. Set vacío = BYOID apagado para el tenant.
    externalScopeLevels: z.array(z.enum(BYOID_LEVEL_VALUES)),
});

// `externalScopeLevels` se re-tipa a `ExternalScopeLevelLocal[]` (el enum completo del contrato): así el
// form interopera sin casts con `currentLevels`/`persistableLevels`/el editor, que hablan ese tipo. El
// `z.enum(BYOID_LEVEL_VALUES)` ya restringe en runtime a los niveles BYOID válidos (un subset del enum).
type FormValues = Omit<z.infer<typeof schema>, 'externalScopeLevels'> & {
    externalScopeLevels: ExternalScopeLevelLocal[];
};

// Igualdad de los sets BYOID independiente del orden (para el diff del PUT). Normalizamos ambos sets
// contra el orden canónico `order` (los niveles disponibles del tipo) y los comparamos como string.
function sameLevels(
    a: ExternalScopeLevelLocal[],
    b: ExternalScopeLevelLocal[],
    order: ExternalScopeLevelLocal[],
): boolean {
    const norm = (xs: ExternalScopeLevelLocal[]): string =>
        order.filter((l) => xs.includes(l)).join(',');
    return norm(a) === norm(b);
}

// Igualdad orden-insensible de los métodos MFA (para el diff de la sección Seguridad).
function sameMethods(a: MfaMethodLocal[], b: MfaMethodLocal[]): boolean {
    const norm = (xs: MfaMethodLocal[]): string =>
        [...xs].sort((x, y) => x.localeCompare(y)).join(',');
    return norm(a) === norm(b);
}

// Opciones del Select del modo de validación: label legible (es) por modo.
const TOKEN_VALIDATION_MODE_OPTIONS = (
    Object.keys(TOKEN_VALIDATION_MODE_META) as TokenValidationModeLocal[]
).map((value) => ({ value, label: TOKEN_VALIDATION_MODE_META[value].label }));

type SectionKey = 'general' | 'security' | 'autoregister';

interface Props {
    tenant: PlatformTenantListItemLocal;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Edición consolidada de un tenant (DEC-RBAC-049), en 3 secciones: General (nombre · validación de
 * tokens · BYOID), Seguridad (2FA on/off · métodos MFA) y Autoregistro (enabled · roles permitidos ·
 * TTL del OTP). Cada sección persiste a su propio endpoint y SOLO cuando cambió (diff/dirty); si nada
 * cambió, no se pega al backend.
 *
 * Permisos (cada sección con el permiso que el BACKEND realmente enforza para su endpoint):
 *  - General → `platform.tenant.update` (PUT /platform/tenants/{id}).
 *  - Seguridad → `tenant.security.policy.manage` scope TENANT/tenantId (PUT /tenants/{id}/security-policy).
 *    Mismo gating que el ex-`SecurityPolicyEditor`: un operador con `platform.tenant.update` pero sin este
 *    permiso NO ve la sección editable (evita el 403 al guardar). Sin él, la pestaña Seguridad se oculta.
 *  - Autoregistro → `platform.tenant.autoregister.manage` (allowlist anti-escalación, solo platform —
 *    DEC-RBAC-050). Sin ese permiso, la pestaña Autoregistro no se muestra.
 * Sin `platform.tenant.update` la sección General cae a solo-lectura (inputs disabled, sin Guardar).
 *
 * Usa el <Modal> del kit (Radix Dialog) + el <Tabs> del kit para el strip de secciones.
 */
export function EditTenantModal({ tenant, open, onOpenChange }: Props) {
    const { hasPermission, hasScopeAccess } = useAuth();
    const canEdit = hasPermission('platform.tenant.update');
    // El endpoint de security-policy lo enforza el backend con `tenant.security.policy.manage` scope
    // TENANT/tenantId (no `platform.tenant.update`). Replicamos el gating del ex-SecurityPolicyEditor
    // (Gate permission + scope-bound) combinando hasPermission + hasScopeAccess.
    const canManageSecurity =
        hasPermission('tenant.security.policy.manage') && hasScopeAccess('TENANT', tenant.tenantId);
    const canManageAutoregister = hasPermission('platform.tenant.autoregister.manage');
    // Puede editar ALGO (cualquier sección). Si no, el modal es solo-lectura (título/badge/aviso).
    const canEditAnything = canEdit || canManageSecurity || canManageAutoregister;

    // Las opciones BYOID salen SIEMPRE de los `levels` de la APP del tenant (mostrando el `displayName`
    // real, ej. "Grupo"), NUNCA del `tenantType` legacy (modelo viejo eliminado). Orden de preferencia:
    //  1) `tenant.levels` si el backend ya enriqueció el detalle con ellos;
    //  2) si no, los `levels` de la app resuelta vía `useApplication(tenant.applicationId)`.
    // Si el tenant no tiene `applicationId` ni `levels` → lista vacía (no se cae al legacy Retailer/Tienda).
    // Tras la ampliación del contrato (`externalScopeLevels: PermissionScope[]` incluye `LEVEL_1/2/3`),
    // TODOS los niveles de la app — legacy o genéricos (`LEVEL_N`) — son persistibles y marcables.
    const appQuery = useApplication(tenant.applicationId);
    const hasTenantLevels = (tenant.levels?.length ?? 0) > 0;
    const byoidOptions: ByoidLevelOption[] = useMemo(() => {
        // `tenant.levels` (backend ya enriqueció) tiene prioridad; si no, los de la app resuelta.
        const levels =
            tenant.levels && tenant.levels.length > 0
                ? tenant.levels
                : (appQuery.data?.levels ?? []);
        return byoidOptionsFromLevels(levels);
    }, [tenant.levels, appQuery.data]);

    if (!open) return null;

    // ── Gate de carga de la APP ──────────────────────────────────────────────────────────────────
    // Si los niveles BYOID dependen de `useApplication` (el tenant NO trae `tenant.levels` poblado pero SÍ
    // tiene `applicationId`), NO montamos el form hasta que la app resolvió. Si no, los `defaultValues` del
    // `useForm` (que se capturan UNA vez al montar) congelarían `externalScopeLevels: []` y, al resolver la
    // app async, el operador vería todo DESMARCADO aunque el tenant tenga niveles → guardar borraría su BYOID.
    // Cuando `tenant.levels` ya viene poblado, o no hay `applicationId`, no hay nada que esperar (monta directo).
    const waitingForApp = !hasTenantLevels && !!tenant.applicationId && appQuery.isLoading;

    return (
        <Modal
            isOpen={open}
            onClose={() => {
                onOpenChange(false);
            }}
            size="lg"
            title={canEditAnything ? 'Editar tenant' : 'Detalle del tenant'}
            titleAction={!canEditAnything ? <Badge variant="info">Solo lectura</Badge> : undefined}
        >
            <div className="space-y-5">
                {/* tenantId copiable, nunca como UUID pelado (anti-id en UI). */}
                <CopyableId label="Tenant ID" value={tenant.tenantId} />
                {waitingForApp ? (
                    <div className="py-10">
                        <Loading size="sm" text="Cargando datos del tenant…" />
                    </div>
                ) : (
                    <EditTenantModalBody
                        tenant={tenant}
                        byoidOptions={byoidOptions}
                        canEdit={canEdit}
                        canManageSecurity={canManageSecurity}
                        canManageAutoregister={canManageAutoregister}
                        canEditAnything={canEditAnything}
                        onOpenChange={onOpenChange}
                    />
                )}
            </div>
        </Modal>
    );
}

interface BodyProps {
    tenant: PlatformTenantListItemLocal;
    /** Opciones BYOID ya resueltas (la app cargó): los `defaultValues` del form se computan con ellas. */
    byoidOptions: ByoidLevelOption[];
    canEdit: boolean;
    canManageSecurity: boolean;
    canManageAutoregister: boolean;
    canEditAnything: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Cuerpo del modal: se monta SOLO cuando las `byoidOptions` ya están resueltas (la app cargó). Así el
 * `useForm` captura `externalScopeLevels` con el valor PERSISTIDO real del tenant (intersectado con los
 * niveles persistibles de la app), y no con `[]` congelado por una resolución async tardía (que borraría
 * el BYOID al guardar). El `<Tabs>`/secciones viven acá; el `<Modal>` y el spinner de carga, en el padre.
 */
function EditTenantModalBody({
    tenant,
    byoidOptions,
    canEdit,
    canManageSecurity,
    canManageAutoregister,
    canEditAnything,
    onOpenChange,
}: BodyProps) {
    const queryClient = useQueryClient();

    const [section, setSection] = useState<SectionKey>('general');
    const [confirmDisableMfaOpen, setConfirmDisableMfaOpen] = useState(false);

    // ── Pre-carga del detalle completo (securityPolicy) ──────────────────────────────────────────
    // El listado (`platformApi.listTenants`) NO trae securityPolicy; el detalle (`getTenant`) sí. Lo
    // cargamos al abrir para inicializar la sección Seguridad con los valores reales del tenant.
    const detailQuery = useQuery({
        queryKey: ['platform', 'tenants', tenant.tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenant.tenantId),
    });
    // Roles del tenant: fuente de las opciones del autoregistro. Solo si la sección es accesible.
    const rolesQuery = useQuery({
        queryKey: ['platform', 'tenants', tenant.tenantId, 'roles'],
        queryFn: () => platformApi.listTenantRoles(tenant.tenantId),
        enabled: canManageAutoregister,
    });
    const tenantRoles = useMemo(() => rolesQuery.data?.items ?? [], [rolesQuery.data]);

    // ── General (react-hook-form) ─────────────────────────────────────────────────────────────────
    const currentMode: TokenValidationModeLocal =
        tenant.tokenValidationMode ?? DEFAULT_TOKEN_VALIDATION_MODE;

    // Orden canónico de los niveles persistibles (los que tienen `byoidValue`): rige el diff y el pre-poblado.
    const persistableLevels: ExternalScopeLevelLocal[] = useMemo(
        () =>
            byoidOptions
                .map((o) => o.byoidValue)
                .filter((v): v is ExternalScopeLevelLocal => v !== undefined),
        [byoidOptions],
    );
    // Valor inicial del BYOID = el `externalScopeLevels` PERSISTIDO del tenant, intersectado con los niveles
    // persistibles de la app (en orden canónico). Como el body monta con `byoidOptions` ya resueltas, este
    // valor refleja el estado real: "Grupo" arranca MARCADO si el tenant ya tiene su nivel (ej. `LEVEL_1`).
    const currentLevels: ExternalScopeLevelLocal[] = persistableLevels.filter((l) =>
        (tenant.externalScopeLevels ?? []).includes(l),
    );

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            displayName: tenant.displayName,
            tokenValidationMode: currentMode,
            externalScopeLevels: currentLevels,
        },
    });
    const generalDirty = form.formState.isDirty;
    const selectedMode = useWatch({ control: form.control, name: 'tokenValidationMode' });
    const selectedLevels = useWatch({ control: form.control, name: 'externalScopeLevels' });

    // ── Seguridad ─────────────────────────────────────────────────────────────────────────────────
    // Estado inicial real del tenant (mfaEnabled default `true` secure-by-default si el GET no lo trae).
    const initialSecurity = useMemo<SecurityDraft>(() => {
        const policy = detailQuery.data?.securityPolicy;
        return {
            mfaEnabled: policy?.mfaEnabled ?? true,
            methodsAllowed: policy?.methodsAllowed ?? [],
        };
    }, [detailQuery.data]);
    // `null` hasta que el user toca la sección: así sabemos si está sucia y qué comparar para el diff.
    const [securityEdit, setSecurityEdit] = useState<SecurityDraft | null>(null);
    const security = securityEdit ?? initialSecurity;
    const securityDirty =
        securityEdit !== null &&
        (securityEdit.mfaEnabled !== initialSecurity.mfaEnabled ||
            !sameMethods(securityEdit.methodsAllowed, initialSecurity.methodsAllowed));

    // ── Autoregistro ──────────────────────────────────────────────────────────────────────────────
    // No hay GET de la config vigente (TenantDetail no la trae): el editor arranca limpio y SOLO persiste
    // cuando el user toca la sección (el PUT es merge-preserve del lado del backend). `null` = intacto.
    const [autoregisterEdit, setAutoregisterEdit] = useState<AutoregisterDraft | null>(null);
    const autoregister = autoregisterEdit ?? EMPTY_AUTOREGISTER;
    const autoregisterDirty = autoregisterEdit !== null;

    // ── Mutations (una por endpoint) ──────────────────────────────────────────────────────────────
    const updateGeneralMut = useMutation({
        mutationFn: (diff: UpdateTenantRequestLocal) =>
            platformApi.updateTenant(tenant.tenantId, diff),
    });
    const updateSecurityMut = useMutation({
        mutationFn: (draft: SecurityDraft) =>
            tenantApi.updateSecurityPolicy(tenant.tenantId, {
                mfaEnabled: draft.mfaEnabled,
                methodsAllowed: draft.methodsAllowed,
            }),
    });
    const updateAutoregisterMut = useMutation({
        mutationFn: (draft: AutoregisterDraft) =>
            tenantApi.updateAutoregister(tenant.tenantId, autoregisterDraftToBody(draft)),
    });

    const isSaving =
        updateGeneralMut.isPending ||
        updateSecurityMut.isPending ||
        updateAutoregisterMut.isPending;

    const isDirty = generalDirty || securityDirty || autoregisterDirty;

    // Bloqueos de validación que impiden guardar (botón disabled, no oculto, para que el user vea por qué).
    const autoregisterHasUnknownRole =
        autoregisterDirty && unknownAutoregisterRoleIds(autoregister, tenantRoles).length > 0;
    const saveBlocked =
        (securityDirty && securityCannotSave(security)) || autoregisterHasUnknownRole;

    // Persiste el diff de cada sección sucia. Secuencial para que un error temprano no deje a medias sin
    // feedback; los toasts/invalidate van al final si todo salió OK.
    const persist = async (): Promise<void> => {
        try {
            if (generalDirty) {
                const v = form.getValues();
                const diff: UpdateTenantRequestLocal = {};
                if (v.displayName !== tenant.displayName) diff.displayName = v.displayName;
                if (v.tokenValidationMode !== currentMode) {
                    diff.tokenValidationMode = v.tokenValidationMode;
                }
                if (!sameLevels(v.externalScopeLevels, currentLevels, persistableLevels)) {
                    diff.externalScopeLevels = v.externalScopeLevels;
                }
                if (Object.keys(diff).length > 0) await updateGeneralMut.mutateAsync(diff);
            }
            if (securityDirty) await updateSecurityMut.mutateAsync(security);
            if (autoregisterDirty) await updateAutoregisterMut.mutateAsync(autoregister);

            toast.success('Tenant actualizado');
            await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
            onOpenChange(false);
        } catch (err) {
            handleApiError(err);
        }
    };

    const onSave = (): void => {
        // Guard defensivo: sin ningún permiso de edición no se persiste (el backend re-enforza).
        if (!canEdit && !canManageSecurity && !canManageAutoregister) return;
        if (saveBlocked) return;
        // Apagar 2FA debilita la seguridad → confirmación previa (mismo criterio que el ex-editor).
        if (securityDirty && !security.mfaEnabled && initialSecurity.mfaEnabled) {
            setConfirmDisableMfaOpen(true);
            return;
        }
        void persist();
    };

    const tabs: { key: SectionKey; label: string }[] = [
        { key: 'general', label: 'General' },
        // La pestaña Seguridad solo aparece con `tenant.security.policy.manage` scope TENANT/tenantId
        // (el permiso que el backend enforza para /security-policy) — evita 403 al guardar.
        ...(canManageSecurity ? [{ key: 'security' as const, label: 'Seguridad' }] : []),
        // La pestaña Autoregistro solo aparece con su permiso (allowlist anti-escalación, solo platform).
        ...(canManageAutoregister ? [{ key: 'autoregister' as const, label: 'Autoregistro' }] : []),
    ];

    return (
        <>
            <div className="space-y-5">
                <Tabs
                    tabs={tabs}
                    activeTab={section}
                    onChange={(key) => {
                        setSection(key as SectionKey);
                    }}
                />

                {section === 'general' && (
                    <div className="space-y-5">
                        <Input
                            label="Nombre"
                            disabled={!canEdit}
                            {...form.register('displayName')}
                            error={form.formState.errors.displayName?.message}
                        />

                        {/* DEC-RBAC-002: modo de validación de tokens. */}
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-1">
                                <Label>Validación de tokens</Label>
                                <HelpTooltip content="Cómo se validan los tokens: offline (solo firma, sin backend), online_preferred (consulta backend; si falla, cae a offline), online_strict (siempre exige backend)." />
                            </div>
                            <Select
                                options={TOKEN_VALIDATION_MODE_OPTIONS}
                                value={selectedMode}
                                onChange={(value) => {
                                    form.setValue(
                                        'tokenValidationMode',
                                        value as TokenValidationModeLocal,
                                        { shouldDirty: true, shouldValidate: true },
                                    );
                                }}
                                disabled={!canEdit}
                                error={form.formState.errors.tokenValidationMode?.message}
                            />
                            <p className="text-xs text-muted-foreground">
                                {TOKEN_VALIDATION_MODE_META[selectedMode].description}
                            </p>
                            {selectedMode === 'online_strict' && (
                                <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                                    ⚠️ Si Cognito deja de responder, los usuarios de este tenant no
                                    podrán operar.
                                </p>
                            )}
                        </div>

                        {/* DEC-RBAC-027: IDs externos (BYOID). */}
                        <ExternalScopeLevelsEditor
                            options={byoidOptions}
                            value={selectedLevels}
                            disabled={!canEdit}
                            onChange={(next) => {
                                form.setValue('externalScopeLevels', next, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                        />
                    </div>
                )}

                {section === 'security' && canManageSecurity && (
                    <EditTenantSecuritySection
                        value={security}
                        disabled={isSaving}
                        onChange={setSecurityEdit}
                    />
                )}

                {section === 'autoregister' && canManageAutoregister && (
                    <EditTenantAutoregisterSection
                        value={autoregister}
                        disabled={isSaving}
                        tenantRoles={tenantRoles}
                        onChange={setAutoregisterEdit}
                    />
                )}

                {!canEditAnything && (
                    <p className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                        No tienes permiso para editar este tenant. Estás viendo sus datos en modo
                        solo lectura.
                    </p>
                )}

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        {canEditAnything ? 'Cancelar' : 'Cerrar'}
                    </Button>
                    {/* Guardar visible solo con algún permiso de edición y cuando hay cambios. */}
                    {canEditAnything && isDirty && (
                        <Button
                            type="button"
                            isLoading={isSaving}
                            disabled={saveBlocked || isSaving}
                            onClick={onSave}
                        >
                            Guardar
                        </Button>
                    )}
                </div>
            </div>

            <ConfirmDialog
                open={confirmDisableMfaOpen}
                onOpenChange={setConfirmDisableMfaOpen}
                title="¿Apagar la autenticación en dos pasos?"
                description="Los usuarios de este tenant van a poder iniciar sesión solo con contraseña, sin segundo factor. Esto reduce la seguridad de las cuentas del tenant."
                confirmLabel="Apagar 2FA"
                cancelLabel="Cancelar"
                destructive
                loading={isSaving}
                onConfirm={() => {
                    setConfirmDisableMfaOpen(false);
                    void persist();
                }}
            />
        </>
    );
}
